import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, RotateCcw } from 'lucide-react';
import authFetch from '../../utils/authFetch';

// HARDCODED FIX: Smartly switch between Localhost and Secure Server
const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_BASE = isLocal ? 'http://localhost:5000' : 'https://bdinteldb.com';

const RawDataTable = ({ endpoint, title }) => {
  // Data States
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  
  // UI States
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Search States
  const [headers, setHeaders] = useState([]);
  const [searchCol, setSearchCol] = useState('');
  const [searchText, setSearchText] = useState('');

  // --- FETCH FUNCTION ---
  const fetchData = (isSearch = false) => {
    setLoading(true);
    setError(null);

    // Build URL with Query Params if searching
    let url = `${API_BASE}${endpoint}`;
    if (isSearch && searchCol && searchText) {
      url += `?column=${encodeURIComponent(searchCol)}&search=${encodeURIComponent(searchText)}`;
    }

    authFetch(url)
      .then(res => {
        if (!res.ok) throw new Error(`Server Error: ${res.status}`);
        return res.json();
      })
      .then(response => {
        // FIXED: Extract rows and total from the new response format
        // API now returns { total: 123, rows: [...] }
        const rows = response.rows || [];
        
        setData(rows);
        setTotalCount(response.total || 0);

        // If it's the first load, grab headers for the dropdown
        if (rows.length > 0 && headers.length === 0) {
          // Filter out the 'raw_data' column if it exists to keep UI clean
          const keys = Object.keys(rows[0]).filter(k => k !== 'raw_data');
          setHeaders(keys);
          setSearchCol(keys[0]); 
        }
        setLoading(false);
      })
      .catch(err => {
        console.error("Fetch error:", err);
        setError(err.message);
        setLoading(false);
      });
  };

  // Initial Load
  useEffect(() => {
    setSearchText(''); 
    setHeaders([]); 
    fetchData(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint]);

  // Handle Search Submit
  const handleSearch = (e) => {
    e.preventDefault();
    fetchData(true);
  };

  // Handle Clear
  const handleClear = () => {
    setSearchText('');
    fetchData(false);
  };

  if (loading && headers.length === 0) return <div style={{ padding: '20px' }}>Loading Data...</div>;
  if (error) return <div style={{ padding: '20px', color: 'red' }}>Error: {error}</div>;

  return (
    <div>
      <Link 
        to="/real-estate/raw" 
        style={{ display: 'block', marginBottom: '20px', color: '#64748b', textDecoration: 'none', fontSize: '14px' }}
      >
        ← Back to Menu
      </Link>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'end', marginBottom: '20px' }}>
        <div>
          <h1 style={{ color: '#1e293b', margin: 0 }}>{title}</h1>
          <p style={{ color: '#64748b', marginTop: '5px' }}>
             Found <b>{totalCount.toLocaleString()}</b> records.
             {data.length < totalCount && <span> (Showing top {data.length})</span>}
          </p>
        </div>
      </div>

      {/* --- SEARCH BAR --- */}
      <form onSubmit={handleSearch} style={searchBarStyle}>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <label style={labelStyle}>Column:</label>
            <select 
              value={searchCol} 
              onChange={(e) => setSearchCol(e.target.value)}
              style={inputStyle}
            >
              {headers.map(h => (
                <option key={h} value={h}>{h.replace(/_/g, ' ')}</option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
            <label style={labelStyle}>Search Query:</label>
            <input 
              type="text" 
              placeholder="Type to search..." 
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ ...inputStyle, width: '100%' }}
            />
          </div>

          <button type="submit" style={buttonStyle}>
            <Search size={16} /> Search
          </button>
          
          {searchText && (
            <button type="button" onClick={handleClear} style={clearButtonStyle}>
              <RotateCcw size={16} /> Reset
            </button>
          )}

        </div>
      </form>

      {/* --- TABLE --- */}
      <div style={{ background: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
          <thead style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
            <tr>
              {headers.map((key) => (
                <th key={key} style={thStyle}>
                  {key.replace(/_/g, ' ')} 
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {data.length === 0 ? (
              <tr><td colSpan={headers.length} style={{padding: '20px', textAlign: 'center'}}>No results found.</td></tr>
            ) : (
              data.map((row, rowIndex) => (
                <tr key={rowIndex} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  {headers.map((key) => {
                    const val = row[key];
                    
                    // Simple logic to format Objects/Arrays if they appear
                    let displayVal = val;
                    if (typeof val === 'object' && val !== null) {
                        displayVal = JSON.stringify(val);
                    }
                    
                    return (
                      <td key={key} style={tdStyle}>
                        {displayVal !== null && displayVal !== undefined ? displayVal : '-'}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// --- STYLES ---
const thStyle = { padding: '12px 16px', fontSize: '12px', fontWeight: '700', color: '#475569', whiteSpace: 'nowrap', textTransform: 'uppercase' };
const tdStyle = { padding: '12px 16px', color: '#334155', whiteSpace: 'nowrap' };
const searchBarStyle = { backgroundColor: '#fff', padding: '15px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #e2e8f0', display: 'inline-block', minWidth: '600px' };
const inputStyle = { padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none' };
const labelStyle = { fontSize: '11px', fontWeight: 'bold', color: '#64748b', marginBottom: '4px', textTransform: 'uppercase' };
const buttonStyle = { marginTop: '18px', padding: '9px 16px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '500' };
const clearButtonStyle = { marginTop: '18px', padding: '9px 16px', backgroundColor: '#fff', color: '#64748b', border: '1px solid #cbd5e1', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' };

export default RawDataTable;